import React, { useRef, useState } from 'react';
import { Div, Heading } from '@kizi/core-components';

import { ExperienceCollectiveCard } from './ExperienceCollectiveCard';
import {
  ExperienceCollectiveCarouselProps,
  CarousellDots,
} from './ExperienceCollectiveCarousel';

// For safari scrollIntoView and behavior: smooth support
import smoothscroll from 'smoothscroll-polyfill';
const isSafari = () =>
  /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
isSafari() && smoothscroll.polyfill();

export interface ExperienceCollectiveSliderProps
  extends Omit<ExperienceCollectiveCarouselProps, 'elementsPerSwipe'> {}

export const ExperienceCollectiveSlider: React.FC<ExperienceCollectiveSliderProps> =
  ({ title, reviews }) => {
    const [activeDot, setActiveDot] = useState(0);

    let pageRef = reviews.map(() => useRef<HTMLDivElement>(null));
    let ref = reviews.map(() => useRef<HTMLDivElement>(null)) as any;

    const scrollToRef = (reviewRef) =>
      reviewRef.current.scrollIntoView({ behavior: 'smooth' });
    const scrollToPane = (num: number) => scrollToRef(pageRef[num]);

    const handleDotClick = (index: number) => {
      scrollToPane(index);
      setActiveDot(index);
    };

    return (
      <Div bg="second.2" py={7} px={4} mt={5} variant="fullWidthOfScreen">
        <Heading variant="H2" as="h2" mb={3} textAlign="center">
          {title}
        </Heading>

        <Div
          py={3}
          display="flex"
          overflowX="auto"
          variant="experienceCollectiveSliderContainer"
          ref={ref}
          data-testid="scrollContainer"
          onScroll={() => {
            // Check each element inside slider if is viewport. If so, we consider its index to be active
            pageRef.forEach((box, index) => {
              const rect = box.current?.getBoundingClientRect() as any;

              const isInViewport =
                rect.left >= 0 &&
                rect.right <=
                  (window.innerWidth || document.documentElement.clientWidth);

              if (isInViewport) {
                setActiveDot(index);
              }
            });
          }}
        >
          {reviews.map(({ name, text, date }, index) => (
            <Div display="flex" minWidth="260px" ref={pageRef[index]}>
              <ExperienceCollectiveCard
                name={name}
                text={text}
                date={date}
                key={text}
                css={{
                  scrollSnapAlign: 'start',
                }}
              />
            </Div>
          ))}
        </Div>

        <Div textAlign="center">
          <CarousellDots
            totalSwipes={reviews.length}
            activeDot={activeDot}
            handleClick={handleDotClick}
          />
        </Div>
      </Div>
    );
  };
